import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { BadgeShelf } from "@/components/BadgeShelf";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Star, Sparkles, ExternalLink, Users, Smile, HeartPulse, Camera, User, Phone, Mail, Repeat } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { levelFor } from "@/lib/badges";
import { CompositeMetricCards } from "@/components/CompositeMetricCards";
import { PointsLedger } from "@/components/PointsLedger";

export default function ParentDashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [students, setStudents] = useState([]);
  const [tips, setTips] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolData, setSchoolData] = useState(null);
  const [gallery, setGallery] = useState([]);

  const loadAll = async () => {
    try {
      const [s, t, sc] = await Promise.all([
        http.get("/students"),
        http.get("/preventive"),
        http.get("/schools"),
      ]);
      setStudents(s.data);
      setTips(t.data);
      setSchools(sc.data);
      if (s.data.length > 0) {
        const schId = s.data[0].school_id;
        const [sd, gl] = await Promise.all([
          http.get(`/schools/${schId}`),
          http.get(`/gallery/${schId}`),
        ]);
        setSchoolData(sd.data);
        setGallery(gl.data);
      }
    } catch (e) {
      toast.error("Failed to load dashboard");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const koiSummary = useMemo(() => {
    const totalPoints = students.reduce((sum, s) => sum + (s.points || 0), 0);
    return levelFor(totalPoints);
  }, [students]);

  return (
    <AppShell
      subtitle={`Hello ${user?.name?.split(" ")[0] || "there"}`}
      title="Your family&rsquo;s health, gamified."
      right={
        <div className="flex items-center gap-3">
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Family Level</div>
              <div className="text-sm font-semibold text-slate-800">{koiSummary.current.name}</div>
            </div>
          </div>
        </div>
      }
    >
      <Tabs defaultValue="kids" className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl h-auto flex-wrap" data-testid="parent-tabs">
          <TabsTrigger value="kids" data-testid="tab-kids">Kids Info</TabsTrigger>
          <TabsTrigger value="about" data-testid="tab-about">About KOI</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery">School Gallery</TabsTrigger>
          <TabsTrigger value="preventive" data-testid="tab-preventive">Preventive Healthcare</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account">Accounts</TabsTrigger>
        </TabsList>

        {/* KIDS INFO */}
        <TabsContent value="kids" className="mt-6 space-y-8">
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-xl text-slate-900">Your children</h2>{/* */}
              <div className="text-xs uppercase tracking-widest text-slate-400">{students.length} tagged</div>
            </div>
            {students.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-slate-500">No children tagged yet.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {students.map((s, i) => <KidCard key={s.id} student={s} onOpen={() => nav(`/parent/child/${s.id}`)} idx={i} />)}
              </div>
            )}
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-xl text-slate-900">The 8-Level KOI Journey</h2>
              <div className="text-xs uppercase tracking-widest text-slate-400">Earned across all kids</div>
            </div>
            <BadgeShelf points={koiSummary.points} testId="parent-badge-shelf" />
          </section>

          {/* Points ledger per child */}
          {students.length > 0 && (
            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl text-slate-900">What earned these levels</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {students.map((s) => (
                  <div key={s.id}>
                    <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">{s.name}</div>
                    <PointsLedger entityType="student" entityId={s.id} title="How this child earned points" />
                  </div>
                ))}
              </div>
            </section>
          )}
        </TabsContent>

        {/* ABOUT KOI */}
        <TabsContent value="about" className="mt-6">
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-br from-sky-500 to-indigo-600 text-white p-8">
              <div className="text-xs uppercase tracking-[0.22em] opacity-85">Our Vision</div>
              <div className="font-heading font-bold text-3xl mt-2">Preventive Healthcare — through Screening in School</div>
              <p className="mt-4 max-w-2xl opacity-90">India&rsquo;s first Health Report Card for kids. We screen 22 WHO-aligned parameters every term and gift every child a lifelong wellness habit — one badge at a time.</p>
              <a data-testid="visit-koi-btn" href="https://koi.org.in" target="_blank" rel="noreferrer" className="inline-flex mt-6 items-center gap-2 bg-white text-sky-700 px-4 py-2 rounded-full text-sm font-semibold hover:shadow-md">
                Visit koi.org.in <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </Card>
        </TabsContent>

        {/* SCHOOL GALLERY */}
        <TabsContent value="gallery" className="mt-6 space-y-6">
          {schoolData && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KpiCard
                  label="Completion Rate"
                  value={`${schoolData.kpi.completion_rate}%`}
                  icon={<Users className="w-5 h-5" />}
                  accent="sky"
                  testId="kpi-completion"
                />
                <KpiCard
                  label="Happiness Index"
                  value={`${schoolData.kpi.happiness_index || 0} / 10`}
                  icon={<Smile className="w-5 h-5" />}
                  accent="amber"
                  testId="kpi-happiness"
                />
                <KpiCard
                  label="School BMI (avg)"
                  value={schoolData.kpi.school_bmi || "—"}
                  icon={<HeartPulse className="w-5 h-5" />}
                  accent="emerald"
                  testId="kpi-bmi"
                />
              </div>
              {schoolData.composite_metrics && (
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] font-bold text-sky-600 mb-3">School Composite Metrics</div>
                  <CompositeMetricCards composite={schoolData.composite_metrics} />
                </div>
              )}
            </>
          )}
          <div>
            <h3 className="font-heading font-semibold text-lg text-slate-900 mb-3">Camp moments · {schoolData?.school?.name}</h3>
            {gallery.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-slate-500">No photos yet.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {gallery.map((g) => (
                  <div key={g.id} data-testid={`gallery-item-${g.id}`} className="rounded-2xl overflow-hidden border border-slate-200 bg-white group">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={g.url} alt={g.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="p-3">
                      <div className="text-xs text-slate-500 flex items-center gap-1"><Camera className="w-3 h-3" /> {g.caption}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* PREVENTIVE */}
        <TabsContent value="preventive" className="mt-6">
          {(() => {
            const founders = tips.filter((t) => t.is_founder);
            const others = tips.filter((t) => !t.is_founder);
            return (
              <>
                {founders.length > 0 && (
                  <div className="mb-8" data-testid="founder-videos-strip">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-6 bg-amber-500 rounded-full" />
                      <h2 className="font-heading font-bold text-lg text-slate-900">Founder&apos;s Reels</h2>
                      <span className="text-xs text-slate-500">Quick health tips from Dr. KOI</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {founders.map((t) => (
                        <Card key={t.id} data-testid={`founder-video-${t.id}`} className="overflow-hidden border-amber-200 bg-gradient-to-b from-amber-50/40 to-white">
                          {t.video_url ? (
                            <div className="aspect-video bg-slate-900">
                              <iframe title={t.title} src={t.video_url} className="w-full h-full" allowFullScreen />
                            </div>
                          ) : t.image_url ? (
                            <div className="aspect-video overflow-hidden">
                              <img src={t.image_url} alt={t.title} className="w-full h-full object-cover" />
                            </div>
                          ) : null}
                          <CardHeader className="pb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px] uppercase tracking-widest">Founder</Badge>
                              <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{t.season}</Badge>
                            </div>
                            <CardTitle className="font-heading text-base mt-1">{t.title}</CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <p className="text-xs text-slate-600 leading-relaxed line-clamp-3">{t.body}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                {others.length > 0 && (
                  <>
                    {founders.length > 0 && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-1.5 h-6 bg-sky-500 rounded-full" />
                        <h2 className="font-heading font-bold text-lg text-slate-900">Preventive Tips</h2>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {others.map((t) => (
                        <Card key={t.id} data-testid={`tip-${t.id}`} className="overflow-hidden">
                          {t.video_url ? (
                            <div className="aspect-video bg-slate-900">
                              <iframe title={t.title} src={t.video_url} className="w-full h-full" allowFullScreen />
                            </div>
                          ) : t.image_url ? (
                            <div className="aspect-video overflow-hidden">
                              <img src={t.image_url} alt={t.title} className="w-full h-full object-cover" />
                            </div>
                          ) : null}
                          <CardHeader>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="uppercase tracking-widest text-[10px]">{t.season}</Badge>
                            </div>
                            <CardTitle className="font-heading">{t.title}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-slate-600 leading-relaxed">{t.body}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
                {tips.length === 0 && (
                  <div className="text-center text-sm text-slate-400 py-12" data-testid="preventive-empty">
                    No preventive content yet. Please check back later.
                  </div>
                )}
              </>
            );
          })()}
        </TabsContent>

        {/* ACCOUNTS */}
        <TabsContent value="account" className="mt-6">
          <AccountSettings students={students} onSaved={() => { refresh(); loadAll(); }} />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function KidCard({ student, onOpen, idx }) {
  const g = student.gamification || levelFor(student.points || 0);
  const pct = g.next ? Math.min(100, Math.round(((g.points - g.current.min) / (g.next.min - g.current.min)) * 100)) : 100;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.06 }}
      className="group"
    >
      <Card data-testid={`kid-card-${student.id}`} className="overflow-hidden hover:-translate-y-1 transition-transform duration-200">
        <div className="p-5 flex items-start gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full overflow-hidden" style={{ boxShadow: `0 0 0 3px ${g.current.color}` }}>
              {student.avatar_url ? (
                <img src={student.avatar_url} alt={student.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-sky-100 grid place-items-center text-sky-700 font-bold text-xl">{student.name.charAt(0)}</div>
              )}
            </div>
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow">
              <Star className="w-4 h-4 text-amber-500" fill="currentColor" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg text-slate-900 truncate">{student.name}</div>
            <div className="text-xs font-mono text-slate-500 truncate">{student.kid_id}</div>
            <div className="mt-1 text-xs text-slate-500">Class {student.grade}{student.section}</div>
          </div>
          <Button size="sm" variant="default" className="bg-sky-500 hover:bg-sky-600" onClick={onOpen} data-testid={`view-report-${student.id}`}>
            Open Report <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <div className="px-5 pb-5">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="uppercase tracking-widest text-slate-400 font-bold">Lv {g.current.level}</span>
            <span className="text-slate-800 font-semibold" style={{ color: g.current.color }}>{g.current.name}</span>
          </div>
          <Progress value={pct} className="h-2" />
          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span>{g.points.toLocaleString()} pts</span>
            <span>{g.next ? `${g.points_to_next.toLocaleString()} to ${g.next.name}` : "MAX LEVEL"}</span>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

function KpiCard({ label, value, icon, accent = "sky", testId }) {
  const accents = {
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return (
    <Card data-testid={testId} className="p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl grid place-items-center border ${accents[accent]}`}>{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">{label}</div>
        <div className="font-heading font-bold text-2xl text-slate-900">{value}</div>
      </div>
    </Card>
  );
}

function AccountSettings({ students, onSaved }) {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [studentId, setStudentId] = useState(students?.[0]?.id || "");
  const [prevSchool, setPrevSchool] = useState("");
  const [prevKidId, setPrevKidId] = useState("");
  const [notes, setNotes] = useState("");
  const [myPorts, setMyPorts] = useState([]);

  const loadPorts = async () => {
    try {
      const r = await http.get("/accounts/port");
      setMyPorts(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { loadPorts(); }, []);

  const save = async () => {
    try {
      const patch = {};
      if (name !== user?.name) patch.name = name;
      if (phone !== user?.phone) patch.phone = phone;
      if (email !== user?.email) patch.email = email;
      await http.post("/accounts/update", patch);
      await refresh?.();
      toast.success("Account updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const submitPort = async () => {
    if (!studentId || !prevSchool.trim()) return toast.error("Please pick a child and enter the previous school name");
    try {
      await http.post("/accounts/port", { student_id: studentId, previous_school: prevSchool, previous_kid_id: prevKidId, notes });
      toast.success("Port request submitted — our coordinator will review it soon");
      setPrevSchool(""); setPrevKidId(""); setNotes("");
      loadPorts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Port failed");
    }
  };

  const statusStyle = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
  };
  const childName = (sid) => students.find((s) => s.id === sid)?.name || "—";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><User className="w-4 h-4" /> Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Name</Label>
            <Input data-testid="update-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</Label>
            <Input data-testid="update-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
            <Input data-testid="update-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" />
            <div className="text-[11px] text-slate-500 mt-1">Login OTPs go to this address. Updates automatically re-tag your children.</div>
          </div>
          <Button data-testid="save-profile-btn" onClick={save} className="w-full bg-sky-500 hover:bg-sky-600">Save</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><Repeat className="w-4 h-4" /> Port previous school report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Child</Label>
            <select
              data-testid="port-student-select"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              className="mt-2 w-full border border-slate-200 rounded-md h-10 px-3 bg-white text-sm"
            >
              {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Previous School</Label>
            <Input data-testid="port-prev-school-input" value={prevSchool} onChange={(e) => setPrevSchool(e.target.value)} className="mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Previous KID ID (optional)</Label>
            <Input data-testid="port-prev-kid-id-input" value={prevKidId} onChange={(e) => setPrevKidId(e.target.value)} className="mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Notes</Label>
            <Textarea data-testid="port-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2" />
          </div>
          <Button data-testid="port-submit-btn" onClick={submitPort} className="w-full">Submit request</Button>

          {myPorts.length > 0 && (
            <div className="pt-4 border-t border-slate-100" data-testid="my-port-requests">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">My requests ({myPorts.length})</div>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {myPorts.map((p) => (
                  <div key={p.id} className="rounded-lg border border-slate-200 p-3 bg-slate-50/60" data-testid={`my-port-${p.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div>
                        <div className="font-semibold text-sm text-slate-800">{childName(p.student_id)}</div>
                        <div className="text-[11px] text-slate-500">from <span className="font-medium">{p.previous_school}</span>{p.previous_kid_id && <> · <span className="font-mono">{p.previous_kid_id}</span></>}</div>
                      </div>
                      <Badge variant="outline" className={`uppercase tracking-widest text-[10px] shrink-0 ${statusStyle[p.status] || ""}`}>{p.status}</Badge>
                    </div>
                    {p.notes && <div className="text-[11px] text-slate-500 italic">Your note: &ldquo;{p.notes}&rdquo;</div>}
                    {p.admin_notes && (
                      <div className="text-[11px] text-slate-700 mt-1 pt-1 border-t border-slate-200">
                        <span className="font-bold text-sky-700 uppercase tracking-widest">Coordinator:</span> {p.admin_notes}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 mt-1">Submitted {new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
